import * as validationRules from "./index";

const AND = "AND";
const operators = Object.freeze({
  Equal: "=",
  NotEqual: "!=",
  GreaterThan: ">",
  LessThan: "<",
  GreaterThanOrEqual: ">=",
  LessThanOrEqual: "<=",
  Contains: "contains",
  AgeAbove: "ageAbove",
  AgeBelow: "ageBelow",
  IsIn: "IsIn"
});

const evaluateOperator = (operator, relatedFieldValue, value) => {
  let result = false;
  switch (operator) {
    case operators.Equal:
      result = relatedFieldValue === value;
      break;
    case operators.NotEqual:
      result = relatedFieldValue !== value;
      break;
    case operators.GreaterThan:
      result = relatedFieldValue > value;
      break;
    case operators.LessThan:
      result = relatedFieldValue < value;
      break;
    case operators.GreaterThanOrEqual:
      result = relatedFieldValue >= value;
      break;
    case operators.LessThanOrEqual:
      result = relatedFieldValue <= value;
      break;
    case operators.Contains:
      result = Array.isArray(relatedFieldValue) && relatedFieldValue.includes(value);
      break;
    case operators.AgeAbove: {
      const age = validationRules.calculateAge(relatedFieldValue);
      result = age !== null && age > value;
      break;
    }
    case operators.AgeBelow: {
      const age = validationRules.calculateAge(relatedFieldValue);
      result = age !== null && age < value;
      break;
    }
    case operators.IsIn:
      result = Array.isArray(value) && value.includes(relatedFieldValue);
      break;
    default:
      throw new Error(`Unsupported operator. ${operator}`);
  }
  return result;
};

/**
 * Generic function to evaluate simple conditions
 * @param {string} logic - The logic operator (AND/OR)
 * @param {Array} rules - Array of rules to evaluate
 * @param {Function} getFieldValue - Function to extract field value from data source
 * @returns {boolean} - Evaluation result
 */
const evaluateSimpleCondition = (logic, rules, getFieldValue) => {
  const results = rules.map((rule) => {
    const { operator, relatedFieldName, value } = rule;
    const relatedFieldNameSplit = relatedFieldName.split(".");

    // Get field value using the provided function
    const relatedFieldValue = getFieldValue(relatedFieldNameSplit);

    const result = evaluateOperator(operator, relatedFieldValue, value);
    return result;
  });

  return logic === AND ? results.every(Boolean) : results.some(Boolean);
};

/**
 * Generic function to evaluate conditions with support for both form and formData
 * @param {Object} condition - The condition object
 * @param {Function} getFieldValue - Function to extract field value from data source
 * @returns {boolean} - Evaluation result
 */
const evaluateCondition = (condition, getFieldValue) => {
  if (condition.multiLevel) {
    const { outerLogic, outerRules } = condition.multiLevel;
    const results = outerRules.map((outerRule) => {
      const { logic, rules } = outerRule;
      return evaluateSimpleCondition(logic, rules, getFieldValue);
    });
    return outerLogic === AND ? results.every(Boolean) : results.some(Boolean);
  } else {
    const { logic, rules } = condition;
    return evaluateSimpleCondition(logic, rules, getFieldValue);
  }
};

// Helper functions for specific data sources

/**
 * Get field value from formData object (for initial values)
 * @param {Array} fieldNameSplit - Array of field name parts
 * @param {Object} formData - The formData object
 * @returns {*} - The field value
 */
const getFieldValueFromFormData = (fieldNameSplit, formData) => {
  let relatedFieldValue = formData;
  for (const fieldName of fieldNameSplit) {
    relatedFieldValue = relatedFieldValue?.[fieldName];
  }
  return relatedFieldValue;
};

/**
 * Get field value from antd form instance
 * @param {Array} fieldNameSplit - Array of field name parts
 * @param {Object} form - The antd form instance
 * @returns {*} - The field value
 */
const getFieldValueFromForm = (fieldNameSplit, form) => {
  return form.getFieldValue(fieldNameSplit);
};

// Specific evaluation functions for backward compatibility

/**
 * Evaluate conditions using formData (for initial values)
 * @param {Object} condition - The condition object
 * @param {Object} formData - The formData object
 * @returns {boolean} - Evaluation result
 */
const evaluateInitialValueCondition = (condition, formData) => {
  const getFieldValue = (fieldNameSplit) => getFieldValueFromFormData(fieldNameSplit, formData);
  return evaluateCondition(condition, getFieldValue);
};

/**
 * Evaluate conditions using antd form instance
 * @param {Object} condition - The condition object
 * @param {Object} form - The antd form instance
 * @returns {boolean} - Evaluation result
 */
const evaluateFieldCondition = (condition, form) => {
  const getFieldValue = (fieldNameSplit) => getFieldValueFromForm(fieldNameSplit, form);
  return evaluateCondition(condition, getFieldValue);
};

export { AND, operators, evaluateFieldCondition, evaluateInitialValueCondition };
